import TableCollapse from '@/components/_common/Table/TableCollapse';
import { ConditionalTooltip } from '@/exports/columnCommonComponents';
import { AssemblyTemplateBEResponse } from '@/redux/slices/packagingSlice.types';
import styled from '@emotion/styled';
import { Chip } from '@mui/material';
import { forwardRef } from 'react';
import PolarisIcon from '../_common/PolarisIcon';
import { getOptionColor } from '../Packaging/utils';

type AssemblyTemplateRowExpandProps = {
  isCollapsed: boolean;
  item: AssemblyTemplateBEResponse;
};

const AssemblyTemplatesRowExpand = ({ isCollapsed, item }: AssemblyTemplateRowExpandProps, ref: React.Ref<HTMLDivElement>) => {
  const { data } = item;
  const flattenedOverlays = data.overlays?.flat() || [];
  return (
    <RowExpand isCollapsed={isCollapsed} ref={ref}>
      <OverlaysAndSegmentsPreview>
        <Row>
          <h4>Overlays</h4>
          <ChipBag>
            {flattenedOverlays.length > 0 ? (
              flattenedOverlays.map((overlay, index) => (
                <ConditionalTooltip key={index} title={overlay?.source?.title_name || ''} show={!!overlay?.source} arrow placement="top">
                  <StyledChip
                    label={overlay.label}
                    size="small"
                    kind={getOptionColor(overlay.kind)}
                    avatar={overlay.source ? <PolarisIcon icon={'file_circle'} /> : undefined}
                  />
                </ConditionalTooltip>
              ))
            ) : (
              <NoMessage>No Overlays</NoMessage>
            )}
          </ChipBag>
        </Row>
        <Row>
          <h4>Segments</h4>
          <ChipBag>
            {data.segments?.length > 0 ? (
              (data.segments || [])?.map((segment, index) => (
                <ConditionalTooltip key={`segment-${index}`} title={segment?.source?.title_name} show={!!segment?.source} arrow placement="top">
                  <StyledChip
                    label={segment.label}
                    size="small"
                    kind={getOptionColor(segment.kind)}
                    avatar={segment.source ? <PolarisIcon icon={'file_circle'} /> : undefined}
                  />
                </ConditionalTooltip>
              ))
            ) : (
              <NoMessage>No Segments</NoMessage>
            )}
          </ChipBag>
        </Row>
      </OverlaysAndSegmentsPreview>
    </RowExpand>
  );
};

export default forwardRef(AssemblyTemplatesRowExpand);

const RowExpand = styled(TableCollapse)``;
const OverlaysAndSegmentsPreview = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: ${({ theme }) => theme.spacing(0.75)};

  h4 {
    margin: 0px;
    padding: 0px;
    width: 80px;
  }
`;

const ChipBag = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(0.75)};
`;

const StyledChip = styled(Chip)<{ kind?: string }>`
  padding: ${({ theme }) => theme.spacing(0.25)};
  background-color: ${({ kind }) => kind + '4a'};
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.default)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  & .MuiChip-avatar {
    height: 16px;
    width: 16px;
    margin-right: 0px;
    color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.default)};
  }
`;

const NoMessage = styled.div`
  font-style: italic;
  opacity: 0.3;
  font-size: 0.9rem;
`;
